/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import makeStyles from '@mui/styles/makeStyles';
import React, { FC } from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { getBackgroundColorByType } from '../helpers';
import { App, Contract, ListType, Tenant, ValueOf } from '../types';
import TreeList from './components/TreeList';
import { FetchNextPageOptions, InfiniteData, InfiniteQueryObserverResult } from 'react-query';
import SearchField from '../Search/SearchField';

type ListSectionProps = {
	type: ValueOf<typeof ListType>;
	title: string;
	isLoading: boolean;
	showSelectAll?: boolean;
	allContracts?: Contract[];
	isTenantFilterSet?: boolean;
	isFetchingNextPage: boolean;
	data: InfiniteData<App[] | Tenant[] | Contract[]> | undefined;
	fetchNextPage: (
		options?: FetchNextPageOptions | undefined
	) => Promise<InfiniteQueryObserverResult<App[] | Tenant[] | Contract[], unknown>>;
};

const useStyles = makeStyles(theme => ({
	paper: {
		display: 'flex',
		flexDirection: 'column',
		textAlign: 'center',
		color: theme.palette.text.secondary,
		height: '100%',
		padding: 10,
		backgroundColor: ({ type }: { type: ValueOf<typeof ListType> }): string => getBackgroundColorByType(type)
	}
}));

const ListSection: FC<ListSectionProps> = ({
	type,
	title,
	data,
	showSelectAll = false,
	isTenantFilterSet = false,
	isLoading,
	fetchNextPage,
	isFetchingNextPage
}: ListSectionProps) => {
	const classes = useStyles({ type });

	return (
		<Grid item xs={12} md={4} style={{ height: 750 }}>
			<Paper className={classes.paper} variant="outlined">
				<Typography>{title}</Typography>
				<SearchField placeholder={title} type={type} />

				<TreeList
					isLoading={isLoading}
					type={type}
					fetchNextPage={fetchNextPage}
					data={data}
					isFetchingNextPage={isFetchingNextPage}
					showSelectAll={showSelectAll}
					isTenantFilterSet={isTenantFilterSet}
				/>
			</Paper>
		</Grid>
	);
};

export default ListSection;
